import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'

type Settings = components['schemas']['PaymentSettingsRead']
type Provider = Settings['available_providers'][number]

const YONTEM: Record<string, { label: string; hint: string }> = {
  bank_transfer: {
    label: 'Banka havalesi / EFT',
    hint: 'Üye derneğin hesabına gönderir; tahsilatı sayman panelden onaylar.',
  },
  manual: {
    label: 'Elden ödeme',
    hint: 'Nakit tahsilat ve düzeltme kayıtları için.',
  },
  paytr: { label: 'PayTR', hint: 'Kartlı ödeme.' },
  iyzico: { label: 'İyzico', hint: 'Kartlı ödeme.' },
  kuveyt_turk: { label: 'Kuveyt Türk', hint: 'Kartlı ödeme.' },
  param: { label: 'Param', hint: 'Kartlı ödeme.' },
}

/**
 * Ödeme ayarları — YES-108.
 *
 * Listede yalnızca `available_providers` görünüyor: adaptörü yazılmamış bir
 * yöntemi seçenek olarak göstermek, dernek onu açtığını sanıp hiçbir tahsilat
 * alamaması demek olurdu.
 */
export function OdemeAyarlari() {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const [secili, setSecili] = useState<Provider[] | null>(null)

  const ayarlar = useQuery({
    queryKey: ['payment-settings', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/payment-settings')
      return data
    },
  })

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      const acik = secili ?? ayarlar.data?.enabled_providers ?? []
      const varsayilan = String(form.get('default_provider') || '')
      const { data } = await api.PUT('/api/v1/payment-settings', {
        body: {
          enabled_providers: acik,
          default_provider: (varsayilan || null) as Settings['default_provider'],
          test_mode: form.get('test_mode') === 'on',
          bank_name: String(form.get('bank_name') || '') || null,
          bank_account_name: String(form.get('bank_account_name') || '') || null,
          iban: String(form.get('iban') || '') || null,
        },
      })
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payment-settings', slug] })
      setHatalar({})
      toast({ title: 'Tahsilat ayarları kaydedildi', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  const veri = ayarlar.data

  // Hata durumu yüklenmeden AYRI ele alınıyor. `!veri` ile tek başına
  // "Yükleniyor…" göstermek, 403 alan kullanıcıyı sonsuza kadar dönen bir
  // ekranda bırakıyordu; tarayıcıda tam olarak bu görüldü.
  if (ayarlar.isError) {
    const yetkisiz = ayarlar.error instanceof ApiError && ayarlar.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody>
            <EmptyState
              icon={<Icon name="shield-alert" size={24} aria-hidden />}
              title={yetkisiz ? 'Bu ekranı görme yetkiniz yok' : 'Ayarlar yüklenemedi'}
              description={
                yetkisiz
                  ? 'Ödeme sağlayıcı ayarları ayrı bir yetki ister; dernek yöneticinizden isteyebilirsiniz.'
                  : 'Bağlantınızı kontrol edip tekrar deneyin.'
              }
              action={
                yetkisiz ? undefined : (
                  <Button variant="secondary" onClick={() => void ayarlar.refetch()}>
                    Tekrar dene
                  </Button>
                )
              }
            />
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  if (!veri) {
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</CardBody>
        </Card>
      </Sayfa>
    )
  }

  // `enabled_providers` sunucuda varsayilanli, yani sema tarafinda opsiyonel.
  const acikYontemler = secili ?? veri.enabled_providers ?? []
  const havaleAcik = acikYontemler.includes('bank_transfer')

  const degistir = (provider: Provider, acik: boolean) => {
    const sonraki = acik
      ? [...acikYontemler, provider]
      : acikYontemler.filter((item) => item !== provider)
    setSecili(sonraki)
  }

  return (
    <Sayfa>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <Card>
          <CardHeader
            title="Ödeme yöntemleri"
            description="Üyenin ödeme sayfasında göreceği yöntemler."
          />
          <CardBody className="flex flex-col gap-3">
            {veri.available_providers.map((provider) => {
              const bilgi = YONTEM[provider] ?? { label: provider, hint: '' }
              const isaretli = acikYontemler.includes(provider)
              return (
                <label
                  key={provider}
                  className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ad-border)] px-3 py-2.5"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={isaretli}
                    onChange={(event) => degistir(provider, event.target.checked)}
                  />
                  <span className="flex flex-col">
                    <span className="text-sm font-medium">{bilgi.label}</span>
                    <span className="text-xs text-[var(--ad-text-muted)]">{bilgi.hint}</span>
                  </span>
                </label>
              )
            })}

            <Field label="Varsayılan yöntem" error={hatalar.default_provider}>
              {(props) => (
                <select
                  name="default_provider"
                  defaultValue={veri.default_provider ?? ''}
                  className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
                  {...props}
                >
                  <option value="">Seçilmedi</option>
                  {acikYontemler.map((provider) => (
                    <option key={provider} value={provider}>
                      {YONTEM[provider]?.label ?? provider}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="test_mode" defaultChecked={veri.test_mode} />
              Test modu — sağlayıcıların sandbox anahtarları kullanılır
            </label>
          </CardBody>
        </Card>

        {havaleAcik && (
          <Card>
            <CardHeader
              title="Havale hesabı"
              description="Üyeye bu bilgiler gösterilir; açıklamaya sipariş numarası yazılır."
            />
            <CardBody className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Banka" error={hatalar.bank_name}>
                  {(props) => (
                    <Input name="bank_name" defaultValue={veri.bank_name ?? ''} {...props} />
                  )}
                </Field>
                <Field label="Hesap adı" error={hatalar.bank_account_name}>
                  {(props) => (
                    <Input
                      name="bank_account_name"
                      defaultValue={veri.bank_account_name ?? ''}
                      {...props}
                    />
                  )}
                </Field>
              </div>
              <Field
                label="IBAN"
                required
                error={hatalar.iban}
                hint="Boşluklu yazabilirsiniz. Kaydederken doğrulanır — yanlış IBAN, aidatların hiçbir yere gitmemesi demek."
              >
                {(props) => (
                  <Input
                    name="iban"
                    defaultValue={veri.iban ?? ''}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="font-mono"
                    {...props}
                  />
                )}
              </Field>
            </CardBody>
          </Card>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={kaydet.isPending}>
            {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
          {veri.test_mode && <Badge tone="warning">Test modu açık</Badge>}
        </div>
      </form>

      <KartUyarisi />
    </Sayfa>
  )
}

/** Başlık + gövde; üç dönüş yolunun da aynı iskeleti kullanması için. */
function Sayfa({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ödeme Ayarları</h1>
      {children}
    </div>
  )
}

/**
 * Kart sağlayıcılarının neden listede olmadığını söylüyor.
 *
 * Boş bir liste bırakmak, derneğin "bende neden görünmüyor?" diye desteğe
 * yazmasına yol açardı; sebebini burada yazmak o soruyu ortadan kaldırıyor.
 */
function KartUyarisi() {
  return (
    <Card>
      <CardBody className="flex items-start gap-3 text-sm">
        <Icon name="shield-alert" size={20} primaryColor="var(--ad-warning)" aria-hidden />
        <div>
          <p className="font-medium">Kartlı ödeme henüz açılmadı</p>
          <p className="mt-0.5 text-xs text-[var(--ad-text-muted)]">
            PayTR, İyzico ve Kuveyt Türk entegrasyonları, sağlayıcıların test hesapları açılıp imza
            doğrulaması gerçek ortamda sınandıktan sonra devreye alınacak. Aidat tahsilatı bu arada
            havale ve elden ödemeyle yapılabilir.
          </p>
        </div>
      </CardBody>
    </Card>
  )
}
